import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import '../styles/chatPage.css';
import SingleVRMViewer from './SingleVRMViewer';
import { useMeeting, usePubSub, useParticipant } from '@videosdk.live/react-sdk';
import { TIMER_CONFIG, CHAT_CONFIG, UI_CONFIG } from '../constants/config';
import avatarAssets from '../utils/avatarAssets';
import apiUtils from '../utils/apiUtils';
// Firebase Analytics imports
import { trackEvent } from '../firebase/analytics';
import { ANALYTICS_EVENTS } from '../firebase/analyticsEvents';
import Live2DViewer from './Live2DViewer';
import Live2DViewerMobile from './Live2DViewerMobile';
import { authAPI, API_BASE_URL } from '../utils/apiUtils';

// Helper function to get user information from localStorage
const getUserInfo = () => {
  try {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      const user = JSON.parse(savedUser);
      return {
        username: user?.username || user?.name || 'Unknown',
        email: user?.email || 'Unknown',
        user_id: user?.id || 'Unknown'
      };
    }
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
  }
  return {
    username: 'Unknown',
    email: 'Unknown', 
    user_id: 'Unknown'
  };
};

// Custom hook for declarative intervals, ensures re-renders
function useInterval(callback, delay) {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    // Don't schedule if no delay is specified.
    if (delay !== null) {
      const id = setInterval(() => {
        if (savedCallback.current) {
          savedCallback.current();
        }
      }, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

// Audio component to handle participant audio streams
const ParticipantAudio = ({ participantId }) => {
  const audioRef = useRef(null);
  const { micOn, micStream, isLocal } = useParticipant(participantId);
  
  useEffect(() => {
    if (audioRef.current) {
      // Always clear the previous stream first
      audioRef.current.srcObject = null;
      
      // Only set up audio for remote participants with active mic and stream
      if (micStream && micOn && !isLocal) {
        // Reduced logging frequency - only log on initial setup
        if (!audioRef.current.srcObject) {
          console.log('[Audio Debug] Setting up audio for participant:', participantId);
        }
        
        const mediaStream = new MediaStream();
        mediaStream.addTrack(micStream.track);
        audioRef.current.srcObject = mediaStream;
        
        // Set audio properties for better echo cancellation
        audioRef.current.volume = 1.0;
        audioRef.current.muted = false;
        
        audioRef.current.play().catch(e => {
          console.error('[Audio Debug] Error playing audio for participant:', participantId, e);
        });
      }
    }
  }, [micStream, micOn, isLocal, participantId]);
  
  // Don't render audio element for local participant to avoid echo
  if (isLocal) return null;
  
  return (
    <audio
      ref={audioRef}
      autoPlay
      playsInline
      style={{ display: 'none' }}
      // Disable any local audio processing that could cause echo
      muted={false}
    />
  );
};

// Isolated component for opponent name display
const OpponentNameDisplay = ({ remoteParticipant }) => {
  const [opponentName, setOpponentName] = useState('Opponent');
  
  useEffect(() => {
    if (remoteParticipant?.displayName) {
      setOpponentName(remoteParticipant.displayName);
    } else {
      setOpponentName('Opponent');
    }
  }, [remoteParticipant?.displayName]);
  
  return opponentName;
};

// Isolated component for user name display that doesn't depend on participant state
const UserNameDisplay = () => {
  const [userName, setUserName] = useState('You');
  
  // Get the user name from localStorage and keep it stable
  useEffect(() => {
    const updateUserName = () => {
      try {
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          const user = JSON.parse(savedUser);
          const displayName = user?.username || user?.name || 'You';
          setUserName(displayName);
        } else {
          setUserName('You');
        }
      } catch (error) {
        console.error('UserNameDisplay - Error parsing user from localStorage:', error);
        setUserName('You');
      }
    };
    
    // Set initial value
    updateUserName();
    
    // Listen for storage changes (in case user changes username in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        updateUserName();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  return userName;
};



// Reaction Particles Component using tsparticles
const ReactionParticles = ({ reaction, side, onComplete }) => {
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    // Generate 8 particles with longer gaps between spawns
    const particleCount = 8;
    const newParticles = [];
    
    for (let i = 0; i < particleCount; i++) {
      // Create more variety in spawn positions within the respective area only
      const spawnPosition = (i / (particleCount - 1)) * 40; // Spread from 0% to 40% within the area
      const baseX = side === 'left' ? '5vw' : '55vw'; // Start position within each area using viewport units
      const finalX = `calc(${baseX} + ${spawnPosition}vw)`; // Distribute within the area boundaries using viewport units
      
      newParticles.push({
        id: i,
        x: finalX,
        y: 0,
        scale: Math.random() * 0.5 + 0.8, // Scale range 0.8 to 1.3
        spawnDelay: i * 0.5, // Each particle spawns 0.5s after the previous
        rotation: Math.random() * 100 - 50, // Gentle rotation
        opacity: Math.random() * 0.3 + 0.7, // Opacity range 0.7 to 1.0
        animation: 'reactionParticleFloat' // All particles use the same upward animation
      });
    }
    
    setParticles(newParticles);
    
    // Call onComplete after all particles have spawned and animated
    const totalDuration = (particleCount * 0.5) + 5; // Spawn time + animation time
    const timer = setTimeout(() => {
      onComplete();
    }, totalDuration * 1000);
    
    return () => clearTimeout(timer);
  }, [onComplete]);
  
  return (
    <>
      {particles.map((particle) => (
        <div
          key={particle.id}
          style={{
            position: 'absolute',
            left: particle.x,
            bottom: '20%',
            fontSize: '60px',
            zIndex: 1000,
            opacity: 0,
            animation: `${particle.animation} 5s linear ${particle.spawnDelay}s forwards`
          }}
        >
          {reaction}
        </div>
      ))}
    </>
  );
};

// Main ChatPage component (no MeetingProvider/Consumer, assumes parent wraps)
const ChatPage = ({ selectedAvatar, onBack, meetingId }) => {
  const processedMessageIds = useRef(new Set());
  
  // Mobile detection helper
  const isMobile = () => window.innerWidth <= 768;
  const [isMobileView, setIsMobileView] = useState(isMobile());
  
  // Enhanced chat state for real-time messaging
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [remoteUserTyping, setRemoteUserTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showReactions, setShowReactions] = useState(false);
  const [showHomeConfirmation, setShowHomeConfirmation] = useState(false);
  const [localReactions, setLocalReactions] = useState([]);
  const [remoteReactions, setRemoteReactions] = useState([]);
  
  // Chat refs
  const chatAutoCloseTimer = useRef(null);
  const typingTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  
  // Refs for unload handler to access current values
  const currentStateRef = useRef({
    meetingId: null,
    remoteParticipantId: null,
    timerActive: false,
    localParticipantId: null,
    leaveMeeting: null,
    token: null, // Store token in ref for unload access
  });

  // Keep token in ref updated for unload handler
  useEffect(() => {
    const token = localStorage.getItem('backend_jwt_token');
    currentStateRef.current.token = token;
  }, []); // Run once on mount

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(isMobile());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Also update token in ref when it might change (e.g., after login)
  useEffect(() => {
    const updateToken = () => {
      const token = localStorage.getItem('backend_jwt_token');
      currentStateRef.current.token = token;
    };
    
    // Listen for storage changes (in case token is updated in another tab)
    window.addEventListener('storage', updateToken);
    
    // Also check token periodically (every 30 seconds) to catch any changes
    const tokenCheckInterval = setInterval(() => {
      const currentToken = localStorage.getItem('backend_jwt_token');
      if (currentToken !== currentStateRef.current.token) {
        updateToken();
      }
    }, 30000);
    
    return () => {
      window.removeEventListener('storage', updateToken);
      clearInterval(tokenCheckInterval);
    };
  }, []);
  
  // Timer state - using a more robust approach
  const [timeRemaining, setTimeRemaining] = useState(TIMER_CONFIG.CHAT_DURATION_SECONDS);
  const [timerActive, setTimerActive] = useState(false);
  const [timerHasStarted, setTimerHasStarted] = useState(false); // New state to track if timer has ever started
  const [isLeavingRoom, setIsLeavingRoom] = useState(false);
  
  // Background selection state
  const [showBackgroundSelector, setShowBackgroundSelector] = useState(false);
  const [selectedBackground, setSelectedBackground] = useState(() => {
    // Read from localStorage on initialization
    const savedBackground = localStorage.getItem('selectedBackground');
    if (savedBackground) {
      return savedBackground;
    }
    
    // If no saved background, assign a random one from the available backgrounds
    const defaultBackgrounds = ['bg1', 'bg2', 'bg3', 'bg4', 'bg5', 'bg6', 'bg7'];
    const randomBackground = defaultBackgrounds[Math.floor(Math.random() * defaultBackgrounds.length)];
    return randomBackground;
  });
  
  // Custom backgrounds state
  const [customBackgrounds, setCustomBackgrounds] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  
  // Remote custom backgrounds (for display only, not selection)
  const [remoteCustomBackgrounds, setRemoteCustomBackgrounds] = useState([]);
  
  // Coming Soon popup states
  const [showGamePopup, setShowGamePopup] = useState(false);
  const [showGiftPopup, setShowGiftPopup] = useState(false);
  
  // Available backgrounds - using real background images (memoized for performance)
  const availableBackgrounds = useMemo(() => [
    { id: 'bg1', name: 'Nature Scene', preview: '/images/backgrounds/bg1.jpg' },
    { id: 'bg2', name: 'Urban View', preview: '/images/backgrounds/bg2.jpg' },
    { id: 'bg3', name: 'Sunset Sky', preview: '/images/backgrounds/bg3.jpg' },
    { id: 'bg4', name: 'Ocean Waves', preview: '/images/backgrounds/bg4.jpg' },
    { id: 'bg5', name: 'Mountain Vista', preview: '/images/backgrounds/bg5.jpg' },
    { id: 'bg6', name: 'Forest Path', preview: '/images/backgrounds/bg6.jpg' },
    { id: 'bg7', name: 'City Lights', preview: '/images/backgrounds/bg7.jpg' },
    // Add local custom backgrounds
    ...customBackgrounds,
    // Add remote custom backgrounds (for display only)
    ...remoteCustomBackgrounds
  ], [customBackgrounds, remoteCustomBackgrounds]);

  // Backgrounds for selection UI (only local backgrounds)
  const selectableBackgrounds = useMemo(() => {
    const backgrounds = [
      { id: 'bg1', name: 'Nature Scene', preview: '/images/backgrounds/bg1.jpg' },
      { id: 'bg2', name: 'Urban View', preview: '/images/backgrounds/bg2.jpg' },
      { id: 'bg3', name: 'Sunset Sky', preview: '/images/backgrounds/bg3.jpg' },
      { id: 'bg4', name: 'Ocean Waves', preview: '/images/backgrounds/bg4.jpg' },
      { id: 'bg5', name: 'Mountain Vista', preview: '/images/backgrounds/bg5.jpg' },
      { id: 'bg6', name: 'Forest Path', preview: '/images/backgrounds/bg6.jpg' },
      { id: 'bg7', name: 'City Lights', preview: '/images/backgrounds/bg7.jpg' },
      // Add only local custom backgrounds
      ...customBackgrounds
    ];
    
    return backgrounds;
  }, [customBackgrounds]);
  
  // Background synchronization
  const { publish: publishBackground, messages: backgroundMessages } = usePubSub('background-change');
  const { publish: publishBackgroundRequest, messages: backgroundRequestMessages } = usePubSub('background-request');
  const [remoteUserBackground, setRemoteUserBackground] = useState('bg1');
  const hasPublishedInitialBackground = useRef(false);
  const hasRequestedRemoteBackground = useRef(false);

  // Wrapper function to save background to localStorage
  const updateSelectedBackground = useCallback((backgroundId) => {
    setSelectedBackground(backgroundId);
    localStorage.setItem('selectedBackground', backgroundId);
  }, []);

  // Skip functionality synchronization
  const { publish: publishSkip, messages: skipMessages } = usePubSub('skip-room');
  const hasRequestedSkip = useRef(false);
  const processedSkipMessages = useRef(new Set()); // Track processed skip messages

  // Matchmaking states
  const [isMatchmakingActive, setIsMatchmakingActive] = useState(false);
  const [matchmakingTimer, setMatchmakingTimer] = useState(0);
  const [showNoMatchesPopup, setShowNoMatchesPopup] = useState(false);

  // Initial matchmaking state - show when app loads and no remote participant
  const [isInitialMatchmaking, setIsInitialMatchmaking] = useState(true);
  const [initialMatchmakingCountdown, setInitialMatchmakingCountdown] = useState(0);

  // Timer completion synchronization
  const { publish: publishTimerComplete, messages: timerCompleteMessages } = usePubSub('timer-complete');
  const hasProcessedTimerComplete = useRef(false);

  // Chat messaging synchronization
  const { publish: publishMessage, messages: chatMessages } = usePubSub(CHAT_CONFIG.PUBSUB_CHANNELS.CHAT_MESSAGES);
  const { publish: publishTyping, messages: typingMessages } = usePubSub(CHAT_CONFIG.PUBSUB_CHANNELS.TYPING_INDICATOR);
  const { publish: publishReaction, messages: reactionMessages } = usePubSub('reactions');

  // Memoize the effective avatar to prevent re-renders
  const effectiveAvatar = useMemo(() => {
    const avatar = selectedAvatar || avatarAssets.getDefaultAvatar();
    return avatar;
  }, [selectedAvatar]);



  // Memoize the Live2D path functions to prevent re-renders
  const getLive2dPath = useCallback((avatar) => {
    return avatarAssets.getLive2dModelPath(avatar);
  }, []);

  // Close background selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showBackgroundSelector && !event.target.closest('.background-selector-overlay') && !event.target.closest('.background-btn')) {
        setShowBackgroundSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showBackgroundSelector]);

  // Meeting/participant logic (memoized to prevent re-renders) - MOVED UP BEFORE CALLBACKS
  const { 
    participants, 
    localParticipant: localParticipantFromHook,
    toggleMic,
    localMicOn,
    leave: leaveMeeting
  } = useMeeting();
  const participantIds = useMemo(() => [...participants.keys()], [participants]);
  
  // Use the localParticipant from useMeeting hook directly
  const localParticipantId = localParticipantFromHook?.id;
  const remoteParticipantId = useMemo(() => participantIds.find(id => id !== localParticipantId), [participantIds, localParticipantId]);

  // Debug participant IDs - OPTIMIZED: Only log when participant IDs actually change
  useEffect(() => {
    // Only log if there are actual changes to prevent excessive logging
    if (participantIds.length > 0) {
      console.log('[Participant Debug] Participant IDs updated:', {
        allParticipantIds: participantIds,
        localParticipantId,
        remoteParticipantId,
        participantsCount: participants.size
      });
    }
  }, [participantIds, localParticipantId, remoteParticipantId]); // Removed localParticipantFromHook to reduce re-renders

  // Keep the ref updated with current values for unload handler
  useEffect(() => {
    currentStateRef.current = {
      ...currentStateRef.current, // Preserve existing values (especially token)
      meetingId,
      remoteParticipantId,
      timerActive,
      localParticipantId,
      leaveMeeting
    };
  }, [meetingId, remoteParticipantId, timerActive, localParticipantId, leaveMeeting]);

  // Helper function to call leave room API
  const callLeaveRoomAPI = useCallback(async (reason = 'user-left') => {
    if (!meetingId) {
      return;
    }

    try {
      // Call the existing leave room API from apiUtils
      const response = await apiUtils.roomAPI.leaveRoom(meetingId);
      
      // Track user leaving chat
      const sessionDuration = timerHasStarted ? (TIMER_CONFIG.CHAT_DURATION_SECONDS - timeRemaining) : 0;
      const userInfo = getUserInfo();
      trackEvent(ANALYTICS_EVENTS.USER_LEFT_CHAT, {
        room_id: meetingId,
        leave_reason: reason,
        session_duration: sessionDuration,
        username: userInfo.username,
        email: userInfo.email,
        user_id: userInfo.user_id,
        timestamp: Date.now()
      });
      
      return response;
    } catch (error) {
      console.error('Error calling leave room API:', error);
      
      // For critical unload scenarios, also try sendBeacon as fallback
      if (reason === 'browser-close' || reason === 'page-unload') {
        try {
          const token = currentStateRef.current.token; // Get token from ref instead of localStorage
          
          const leaveData = JSON.stringify({ 
            roomId: meetingId,
            participantId: localParticipantId,
            reason: reason,
            timestamp: Date.now(),
            ...(token && { token: token }) // Include token in body for sendBeacon
          });
          
          // Use the correct API base URL
          const sent = navigator.sendBeacon(`${API_BASE_URL}/api/rooms/${meetingId}/leave`, leaveData);
        } catch (beaconError) {
          console.error('sendBeacon fallback failed:', beaconError);
        }
      }
      
      throw error;
    }
  }, [meetingId, localParticipantId, timerHasStarted, timeRemaining]);

  // Enhanced sendMessage function with PubSub - NOW defined after localParticipantId
  const sendMessage = useCallback(() => {
    if (inputMessage.trim() && inputMessage.length <= CHAT_CONFIG.MAX_MESSAGE_LENGTH && localParticipantId) {
      const newMessage = {
        id: Date.now(),
        text: inputMessage.trim(),
        sender: "user",
        senderId: localParticipantId,
        timestamp: new Date().toISOString(),
        displayTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      
      // Add to local messages
      setMessages(prev => [...prev.slice(-CHAT_CONFIG.MAX_VISIBLE_MESSAGES), newMessage]);
      
      // Publish to remote user
      if (publishMessage && remoteParticipantId) {
        publishMessage(newMessage);
      }
      
      // Track chat message sent
      const userInfo = getUserInfo();
      trackEvent(ANALYTICS_EVENTS.CHAT_MESSAGE_SENT, {
        room_id: meetingId,
        message_length: inputMessage.trim().length,
        username: userInfo.username,
        email: userInfo.email,
        user_id: userInfo.user_id,
        timestamp: Date.now()
      });
      
      setInputMessage("");
      setIsTyping(false);
      
      // Stop typing indicator
      if (publishTyping && remoteParticipantId) {
        publishTyping({ isTyping: false, senderId: localParticipantId });
      }
      
      // Reset auto-close timer
      resetChatAutoClose();
    }
  }, [inputMessage, localParticipantId, remoteParticipantId, publishMessage, publishTyping]);

  // Handle typing indicator - defined after localParticipantId
  const handleTyping = useCallback(() => {
    if (!isTyping && remoteParticipantId && localParticipantId) {
      setIsTyping(true);
      if (publishTyping) {
        publishTyping({ isTyping: true, senderId: localParticipantId });
      }
    }
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set new timeout
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      if (publishTyping && remoteParticipantId && localParticipantId) {
        publishTyping({ isTyping: false, senderId: localParticipantId });
      }
    }, CHAT_CONFIG.TYPING_TIMEOUT_MS);
  }, [isTyping, remoteParticipantId, publishTyping, localParticipantId]);

  // Auto-close chat functionality
  const resetChatAutoClose = useCallback(() => {
    if (chatAutoCloseTimer.current) {
      clearTimeout(chatAutoCloseTimer.current);
    }
    
    if (isChatOpen) {
      chatAutoCloseTimer.current = setTimeout(() => {
        setIsChatOpen(false);
      }, CHAT_CONFIG.AUTO_OPEN_DURATION_MS);
    }
  }, [isChatOpen]);

  // Toggle chat visibility
  const toggleChat = useCallback(() => {
    trackEvent(ANALYTICS_EVENTS.CHAT_TOGGLE_CLICK, {
      room_id: meetingId,
      current_state: isChatOpen ? 'open' : 'closed',
      action: isChatOpen ? 'close' : 'open',
      timestamp: Date.now()
    });
    
    setIsChatOpen(prev => {
      const newState = !prev;
      if (newState) {
        setUnreadCount(0);
        resetChatAutoClose();
      } else {
        if (chatAutoCloseTimer.current) {
          clearTimeout(chatAutoCloseTimer.current);
        }
      }
      return newState;
    });
  }, [resetChatAutoClose, meetingId, isChatOpen]);

  // Scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    } else {
      handleTyping();
    }
  }, [sendMessage, handleTyping]);

  // Handle input change with typing indicator
  const handleInputChange = useCallback((e) => {
    setInputMessage(e.target.value);
    handleTyping();
  }, [handleTyping]);

  // Close background selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showBackgroundSelector && !event.target.closest('.background-selector-overlay') && !event.target.closest('.background-btn')) {
        setShowBackgroundSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showBackgroundSelector]);

  // Timer functionality
  const formatTime = useCallback((seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  // Handle timer completion - leave room and start matchmaking
  const handleTimerComplete = useCallback(async () => {
    if (isLeavingRoom || hasProcessedTimerComplete.current) {
      return; // Prevent multiple calls
    }
    
    setIsLeavingRoom(true);
    setIsMatchmakingActive(true); // Start showing "finding match" UI
    hasProcessedTimerComplete.current = true;
    
    try {
      // Call leave room API first
      await callLeaveRoomAPI('timer-complete');
      
      // Track timer completion
      const sessionDuration = TIMER_CONFIG.CHAT_DURATION_SECONDS - timeRemaining;
      trackEvent(ANALYTICS_EVENTS.TIMER_COMPLETED, {
        room_id: meetingId,
        session_duration: sessionDuration,
        timestamp: Date.now()
      });
      
      // Publish timer completion to other participant so they also leave
      if (publishTimerComplete && remoteParticipantId) {
        publishTimerComplete({ action: 'timer-complete', senderId: localParticipantId });
      }
      
      // Leave the current VideoSDK meeting
      if (leaveMeeting) {
        leaveMeeting();
      }
      
      // Add a small delay to ensure the meeting is properly left
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Call the onBack prop directly with timer completion flags
      // This will trigger the re-matchmaking logic in App.js
      if (onBack) {
        onBack({ timerComplete: true, stayOnPage: true });
      }
    } catch (error) {
      console.error('Error during room transition:', error);
      // If there's an error, try to go back to landing.
      if (onBack) {
        onBack(); // This will trigger the default "go to landing" behavior
      }
      setIsMatchmakingActive(false);
      setIsLeavingRoom(false);
    }
  }, [isLeavingRoom, onBack, leaveMeeting, publishTimerComplete, remoteParticipantId, localParticipantId, callLeaveRoomAPI]);
  
  // Handle skip functionality - optimized for performance
  const handleSkip = useCallback(async () => {
    if (hasRequestedSkip.current) {
      return; // Prevent multiple skip operations
    }
    
    hasRequestedSkip.current = true;

    // Publish skip request to remote participant (non-blocking)
    if (publishSkip) {
      publishSkip({
        senderId: localParticipantId,
        timestamp: Date.now()
      });
    }

    try {
      // First, leave the current meeting properly
      if (leaveMeeting && typeof leaveMeeting === 'function') {
        console.log('[Skip Debug] Leaving current meeting before re-matchmaking');
        leaveMeeting();
        
        // Add a small delay to ensure the meeting is properly left
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Call leave room API (non-blocking)
      if (meetingId) {
        apiUtils.roomAPI.leaveRoom(meetingId).catch(error => {
          console.error('Error leaving room for skip:', error);
        });
      }

      // Now trigger re-matchmaking
      console.log('[Skip Debug] Triggering re-matchmaking');
      onBack({
        timerComplete: true,
        stayOnPage: true,
        reason: 'skip'
      });
    } catch (error) {
      console.error('[Skip Debug] Error during skip:', error);
      // Reset the flag in case of error
      hasRequestedSkip.current = false;
    }
  }, [publishSkip, localParticipantId, meetingId, onBack, leaveMeeting]);

  // Cleanup processed skip messages when meeting changes - optimized
  useEffect(() => {
    // Only cleanup if we have processed messages or skip state
    if (processedSkipMessages.current.size > 0 || hasRequestedSkip.current) {
      processedSkipMessages.current.clear();
      hasRequestedSkip.current = false;
    }
    
    // Reset initial matchmaking countdown when meeting changes
    setInitialMatchmakingCountdown(0);
    initialMatchmakingCountdownRef.current = 0;
  }, [meetingId]);

  // Listen for skip messages from remote participant - optimized
  useEffect(() => {
    if (!skipMessages || skipMessages.length === 0) return;
    
    const lastSkip = skipMessages[skipMessages.length - 1];
    if (lastSkip && lastSkip.senderId && lastSkip.senderId !== localParticipantId) {
      // Check if we've already processed this skip message
      const skipKey = `${lastSkip.senderId}-${lastSkip.timestamp}`;
      if (processedSkipMessages.current.has(skipKey)) {
        return; // Already processed this skip message
      }
      
      // Mark this skip message as processed immediately
      processedSkipMessages.current.add(skipKey);
      
      // Set skip flag to prevent local skip operations
      hasRequestedSkip.current = true;
      
      // Handle remote skip with proper meeting cleanup
      const handleRemoteSkip = async () => {
        try {
          // First, leave the current meeting properly
          if (leaveMeeting && typeof leaveMeeting === 'function') {
            console.log('[Remote Skip Debug] Leaving current meeting before re-matchmaking');
            leaveMeeting();
            
            // Add a small delay to ensure the meeting is properly left
            await new Promise(resolve => setTimeout(resolve, 500));
          }

          // Call leave room API (non-blocking)
          if (meetingId) {
            apiUtils.roomAPI.leaveRoom(meetingId).catch(error => {
              console.error('Error leaving room for remote skip:', error);
            });
          }

          // Now trigger re-matchmaking
          console.log('[Remote Skip Debug] Triggering re-matchmaking');
          onBack({
            timerComplete: true,
            stayOnPage: true,
            reason: 'remote-skip'
          });
        } catch (error) {
          console.error('[Remote Skip Debug] Error during remote skip:', error);
          // Reset the flag in case of error
          hasRequestedSkip.current = false;
        }
      };
      
      handleRemoteSkip();
    }
  }, [skipMessages, localParticipantId, onBack, leaveMeeting, meetingId]);

  // Comprehensive cleanup to prevent memory leaks
  useEffect(() => {
    return () => {
      // Cleanup all timers and intervals
      if (initialMatchmakingTimer.current) {
        clearTimeout(initialMatchmakingTimer.current);
        clearInterval(initialMatchmakingTimer.current);
        initialMatchmakingTimer.current = null;
      }
      
      // Clear any remaining timeouts
      const timeouts = window.setTimeout(() => {}, 0);
      for (let i = 0; i < timeouts; i++) {
        window.clearTimeout(i);
      }
      
      // Clear any remaining intervals
      const intervals = window.setInterval(() => {}, 0);
      for (let i = 0; i < intervals; i++) {
        window.clearInterval(i);
      }
      
      // Clear processed skip messages
      processedSkipMessages.current.clear();
      hasRequestedSkip.current = false;
      
      console.log('[Cleanup] All timers and intervals cleared');
    };
  }, []);

  // Manage initial matchmaking state - optimized
  const hasSetInitialMatchmaking = useRef(false);
  const initialMatchmakingTimer = useRef(null);
  const initialMatchmakingCountdownRef = useRef(0);
  
  useEffect(() => {
    // Clear any existing timer
    if (initialMatchmakingTimer.current) {
      clearTimeout(initialMatchmakingTimer.current);
      initialMatchmakingTimer.current = null;
    }
    
    if (remoteParticipantId) {
      // Remote participant joined, hide initial matchmaking
      if (isInitialMatchmaking) {
        setIsInitialMatchmaking(false);
      }
      hasSetInitialMatchmaking.current = false; // Reset for next time
      initialMatchmakingCountdownRef.current = 0; // Reset countdown
      setInitialMatchmakingCountdown(0); // Reset state
    } else if (!hasSetInitialMatchmaking.current) {
      // No remote participant and we haven't set initial state yet
      hasSetInitialMatchmaking.current = true;
      initialMatchmakingTimer.current = setTimeout(() => {
        setIsInitialMatchmaking(true);
        // Start countdown for skip popup
        const countdownInterval = setInterval(() => {
          initialMatchmakingCountdownRef.current += 1;
          setInitialMatchmakingCountdown(initialMatchmakingCountdownRef.current);
          if (initialMatchmakingCountdownRef.current >= TIMER_CONFIG.MATCHMAKING_DURATION_SECONDS) {
            // Automatically leave the room when max timer is reached
            callLeaveRoomAPI('max-timer-reached');
            leaveMeeting();
            
            setShowNoMatchesPopup(true);
            setIsMatchmakingActive(false);
            return 0;
          }
        }, 1000);
        
        // Store interval for cleanup
        initialMatchmakingTimer.current = countdownInterval;
      }, 2000); // Show after 2 seconds of loading
    }
    
    // Cleanup function
    return () => {
      if (initialMatchmakingTimer.current) {
        clearTimeout(initialMatchmakingTimer.current);
        clearInterval(initialMatchmakingTimer.current);
        initialMatchmakingTimer.current = null;
      }
    };
  }, [remoteParticipantId]);

  // Safe microphone toggle function with additional safety measures
  const handleMicToggle = useCallback(() => {
    try {
      if (toggleMic && typeof toggleMic === 'function') {
        
        // Use VideoSDK's toggleMic which should properly enable/disable the audio stream
        toggleMic();
        
        // Track mic toggle event
        trackEvent(ANALYTICS_EVENTS.MIC_TOGGLE, {
          room_id: meetingId,
          mic_state: !localMicOn ? 'on' : 'off',
          timestamp: Date.now()
        });
        
      } else {
        console.error('[Mic Debug] toggleMic function not available');
      }
    } catch (error) {
      console.error('[Mic Debug] Error toggling microphone:', error);
    }
  }, [toggleMic, localMicOn, meetingId]);
  

  
  // Monitor participant audio states for debugging - OPTIMIZED: Reduced frequency
  useEffect(() => {
    // Only log if there are actual participants and changes
    if (participantIds.length > 0) {
      const audioStates = participantIds.map(id => {
        const participant = participants.get(id);
        return participant ? { id, micOn: participant.micOn, hasStream: !!participant.micStream } : null;
      }).filter(Boolean);
      
      if (audioStates.length > 0) {
        console.log('[Audio Debug] Participant audio states:', audioStates);
      }
    }
  }, [participants]); // Removed participantIds dependency to reduce re-renders
  
  // Get participant objects (memoized)
  const localParticipant = useMemo(() => {
    return localParticipantId ? participants.get(localParticipantId) : null;
  }, [localParticipantId, participants, localParticipantFromHook]);
  const remoteParticipant = useMemo(() => remoteParticipantId ? participants.get(remoteParticipantId) : null, [remoteParticipantId, participants]);
  
  // Extract avatarId from participant metadata or use fallback
  const getParticipantAvatarId = useCallback((participant) => {
    // Try to get avatarId from participant metadata first
    if (participant?.metaData) {
      try {
        const metadata = typeof participant.metaData === 'string' 
          ? JSON.parse(participant.metaData) 
          : participant.metaData;
        if (metadata.avatarId) {
          return metadata.avatarId;
        }
      } catch (error) {
        console.warn('[ChatPage] Error parsing participant metadata:', error);
      }
    }
    
    // Fallback: use local user data for local participant only
    if (participant === localParticipantFromHook) {
      try {
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          const user = JSON.parse(savedUser);
          const fallbackId = user?.avatarId || user?.selectedAvatar?.id || 'm1';
          return fallbackId;
        }
      } catch (error) {
        console.warn('[ChatPage] Error getting local user avatar:', error);
      }
    }
    
    return 'm1'; // Default fallback
  }, [localParticipantFromHook]);

  // Get avatar IDs for both participants
  const localAvatarId = useMemo(() => {
    const avatarId = getParticipantAvatarId(localParticipant);
    return avatarId;
  }, [localParticipant, getParticipantAvatarId]);
  
  const remoteAvatarId = useMemo(() => {
    const avatarId = getParticipantAvatarId(remoteParticipant);
    return avatarId;
  }, [remoteParticipant, getParticipantAvatarId]);



  // --- START: Robust Timer Logic ---

  // This effect starts the timer ONCE when an opponent joins.
  // It will not stop if the opponent leaves.
  useEffect(() => {
    if (remoteParticipantId && !timerHasStarted && !isLeavingRoom) {
      setTimeRemaining(TIMER_CONFIG.CHAT_DURATION_SECONDS); // Reset timer
      setTimerActive(true);   // Start countdown
      setTimerHasStarted(true); // Ensure it only starts once per session
      
      // Track when both users are in the chat (session started)
      const userInfo = getUserInfo();
      trackEvent(ANALYTICS_EVENTS.USER_JOINED_CHAT, {
        room_id: meetingId,
        user_type: 'remote_user',
        username: userInfo.username,
        email: userInfo.email,
        user_id: userInfo.user_id,
        timestamp: Date.now()
      });
    }
  }, [remoteParticipantId, isLeavingRoom, timerHasStarted, meetingId]);

  // This effect resets the state when a new meeting starts (i.e., new meetingId)
  useEffect(() => {
    // Track user joining chat
    if (meetingId) {
      const userInfo = getUserInfo();
      trackEvent(ANALYTICS_EVENTS.USER_JOINED_CHAT, {
        room_id: meetingId,
        user_type: 'local_user',
        username: userInfo.username,
        email: userInfo.email,
        user_id: userInfo.user_id,
        timestamp: Date.now()
      });
    }
    
    // Reset state for the new room
    setIsMatchmakingActive(false);
    setIsLeavingRoom(false);
    setTimerHasStarted(false);
    setTimerActive(false);
    setTimeRemaining(TIMER_CONFIG.CHAT_DURATION_SECONDS);
    // Reset skip flag
    hasRequestedSkip.current = false;
    // Reset timer completion flag
    hasProcessedTimerComplete.current = false;
    // Do not reset hasPublishedInitialBackground or other sync flags here,
    // as they are managed by participant join/leave events.
  }, [meetingId]);

  // This custom hook runs the countdown interval.
  useInterval(() => {
    if (timeRemaining > 0) {
      setTimeRemaining(t => t - 1); // Decrement time
    } else {
      setTimerActive(false); // Timer reached 0, stop the interval
      handleTimerComplete(); // Handle end of match
    }
  }, timerActive ? TIMER_CONFIG.UPDATE_INTERVAL_MS : null); // Run every second, or pause if null

  // --- END: Robust Timer Logic ---

  // Use remote avatar ID from participant metadata
  const finalRemoteAvatarId = useMemo(() => {
    return remoteAvatarId;
  }, [remoteAvatarId]);

  // Memoize Live2D paths to prevent re-renders - use participant avatar IDs
  const localLive2dPath = useMemo(() => {
    const path = getLive2dPath({ id: localAvatarId });
    console.log('[ChatPage] Local Live2D path calculated:', path, 'from avatar ID:', localAvatarId);
    return path;
  }, [localAvatarId, getLive2dPath]);
  
  const remoteLive2dPath = useMemo(() => {
    const path = getLive2dPath({ id: finalRemoteAvatarId });
    console.log('[ChatPage] Remote Live2D path calculated:', path, 'from avatar ID:', finalRemoteAvatarId);
    return path;
  }, [finalRemoteAvatarId, getLive2dPath]);

  // Live2D paths are now passed directly to Live2DViewer component

  // Background sync effects (after participant IDs are available)
  // Listen for remote background changes
  useEffect(() => {
    if (!backgroundMessages || !remoteParticipantId) return; // Only listen when remote user exists
    const lastMsg = backgroundMessages[backgroundMessages.length - 1];
    
    if (lastMsg && lastMsg.senderId && lastMsg.senderId !== localParticipantId) {
      // Extract backgroundId from the message
      const backgroundData = lastMsg.message;
      
      if (backgroundData && backgroundData.backgroundId) {
        // If it's a custom background, add it to remote custom backgrounds for display
        if (backgroundData.backgroundData && backgroundData.backgroundData.isCustom) {
          const customBg = backgroundData.backgroundData;
          
          // Add to remote custom backgrounds if not already present
          setRemoteCustomBackgrounds(prev => {
            const exists = prev.find(bg => bg.id === customBg.id);
            if (!exists) {
              return [...prev, customBg];
            }
            return prev;
          });
        }
        
        setRemoteUserBackground(backgroundData.backgroundId);
      }
    }
  }, [backgroundMessages, remoteParticipantId, localParticipantId]);

  // Clean up remote custom backgrounds when remote user leaves
  useEffect(() => {
    if (!remoteParticipantId) {
      setRemoteCustomBackgrounds([]);
      setRemoteUserBackground('bg1'); // Reset to default background
    }
  }, [remoteParticipantId]);



  // Listen for skip messages from remote participant
  useEffect(() => {
    if (!skipMessages) return; // Listen even when no remote participant (they might have just left)
    const lastMsg = skipMessages[skipMessages.length - 1];
    if (lastMsg && lastMsg.senderId && lastMsg.senderId !== localParticipantId) {
      const skipData = lastMsg.message;
      if (skipData?.action === 'skip' && !hasRequestedSkip.current) {
        // Remote user requested skip, start matchmaking
        setIsMatchmakingActive(true);
        setShowNoMatchesPopup(false);
      }
    }
  }, [skipMessages, localParticipantId]);

  // Listen for timer completion messages from remote participant
  useEffect(() => {
    if (!timerCompleteMessages || !remoteParticipantId) return; // Only listen when remote user exists
    const lastMsg = timerCompleteMessages[timerCompleteMessages.length - 1];
    if (lastMsg && lastMsg.senderId && lastMsg.senderId !== localParticipantId) {
      const timerData = lastMsg.message;
      if (timerData?.action === 'timer-complete' && !hasProcessedTimerComplete.current) {
        handleTimerComplete(); // Auto-complete when remote user's timer completes
      }
    }
  }, [timerCompleteMessages, remoteParticipantId, localParticipantId, handleTimerComplete]);

  // Publish current background when remote user joins (only once)
  useEffect(() => {
    if (remoteParticipantId && publishBackground && !hasPublishedInitialBackground.current) {
      // Find the current background data
      const bg = availableBackgrounds.find(bg => bg.id === selectedBackground);
      
      const backgroundData = {
        backgroundId: selectedBackground,
        senderId: localParticipantId,
        timestamp: Date.now(),
        // Include full background data for custom backgrounds
        backgroundData: bg ? {
          id: bg.id,
          name: bg.name,
          preview: bg.preview,
          fullUrl: bg.fullUrl,
          isCustom: bg.isCustom
        } : null
      };
      
      publishBackground(backgroundData);
      hasPublishedInitialBackground.current = true;
    } else if (!remoteParticipantId) {
      // Reset the flag when remote user leaves
      hasPublishedInitialBackground.current = false;
    }
  }, [remoteParticipantId, publishBackground, selectedBackground, availableBackgrounds, localParticipantId]);

  // Request remote user's background when we join (only once)
  useEffect(() => {
    if (remoteParticipantId && publishBackgroundRequest && !hasRequestedRemoteBackground.current) {
      publishBackgroundRequest({
        senderId: localParticipantId,
        timestamp: Date.now()
      });
      hasRequestedRemoteBackground.current = true;
    } else if (!remoteParticipantId) {
      // Reset the flag when remote user leaves
      hasRequestedRemoteBackground.current = false;
    }
  }, [remoteParticipantId, publishBackgroundRequest, localParticipantId]);

  // Listen for background requests and respond with current background
  useEffect(() => {
    if (!backgroundRequestMessages || !remoteParticipantId) return;
    const lastMsg = backgroundRequestMessages[backgroundRequestMessages.length - 1];
    
    if (lastMsg && lastMsg.senderId && lastMsg.senderId !== localParticipantId) {
      // Find the current background data
      const bg = availableBackgrounds.find(bg => bg.id === selectedBackground);
      
      const backgroundData = {
        backgroundId: selectedBackground,
        senderId: localParticipantId,
        timestamp: Date.now(),
        // Include full background data for custom backgrounds
        backgroundData: bg ? {
          id: bg.id,
          name: bg.name,
          preview: bg.preview,
          fullUrl: bg.fullUrl,
          isCustom: bg.isCustom
        } : null
      };
      
      publishBackground(backgroundData);
    }
  }, [backgroundRequestMessages, remoteParticipantId, localParticipantId, selectedBackground, availableBackgrounds, publishBackground]);

  // Publish background changes to other participants
  const handleBackgroundChange = useCallback((backgroundId) => {
    updateSelectedBackground(backgroundId);
    setShowBackgroundSelector(false);
    
    // Publish background change to remote user
    if (publishBackground && remoteParticipantId) {
      // Find the background data
      const bg = availableBackgrounds.find(bg => bg.id === backgroundId);
      
      const backgroundData = {
        backgroundId,
        senderId: localParticipantId,
        timestamp: Date.now(),
        // Include full background data for custom backgrounds
        backgroundData: bg ? {
          id: bg.id,
          name: bg.name,
          preview: bg.preview,
          fullUrl: bg.fullUrl,
          isCustom: bg.isCustom
        } : null
      };
      publishBackground(backgroundData);
    }
  }, [publishBackground, remoteParticipantId, localParticipantId, availableBackgrounds, updateSelectedBackground]);

  // Helper function to get background style
  const getBackgroundStyle = useCallback((backgroundId) => {
    const bg = availableBackgrounds.find(bg => bg.id === backgroundId);
    if (bg && bg.preview) {
      return { backgroundImage: `url(${bg.preview})` };
    }
    // Default fallback to bg1 if background not found
    return { backgroundImage: 'url(/images/backgrounds/bg1.jpg)' };
  }, [availableBackgrounds]);

  // Listen for incoming chat messages
  useEffect(() => {
    if (!chatMessages || chatMessages.length === 0) return;
    
    const lastMsg = chatMessages[chatMessages.length - 1];
    if (
      lastMsg && 
      lastMsg.senderId && 
      lastMsg.senderId !== localParticipantId &&
      !processedMessageIds.current.has(lastMsg.message.id)
    ) {
      processedMessageIds.current.add(lastMsg.message.id);

      const incomingMessage = {
        ...lastMsg.message,
        sender: "opponent"
      };
      
      // Add to messages
      setMessages(prev => [...prev.slice(-CHAT_CONFIG.MAX_VISIBLE_MESSAGES), incomingMessage]);
      
      // Auto-open chat and increment unread count
      if (!isChatOpen) {
        setIsChatOpen(true);
        setUnreadCount(prev => prev + 1);
        resetChatAutoClose();
      } else {
        resetChatAutoClose();
      }
      
      // Scroll to bottom
      setTimeout(scrollToBottom, 100);
    }
  }, [chatMessages, localParticipantId, isChatOpen, resetChatAutoClose, scrollToBottom]);

  // Listen for typing indicators
  useEffect(() => {
    if (!typingMessages || typingMessages.length === 0) return;
    
    const lastMsg = typingMessages[typingMessages.length - 1];
    if (lastMsg && lastMsg.senderId && lastMsg.senderId !== localParticipantId) {
      const typingData = lastMsg.message;
      setRemoteUserTyping(typingData.isTyping);
    }
  }, [typingMessages, localParticipantId]);

  // Listen for reactions
  useEffect(() => {
    if (!reactionMessages || reactionMessages.length === 0) return;
    
    const lastReaction = reactionMessages[reactionMessages.length - 1];
    if (lastReaction && lastReaction.senderId && lastReaction.senderId !== localParticipantId) {
      const reactionData = lastReaction.message;
      
      // Add to local reactions area (right side) for the receiver's view
      setLocalReactions(prev => [...prev, {
        ...reactionData,
        id: Date.now() + Math.random()
      }]);
    }
  }, [reactionMessages, localParticipantId]);

  // Matchmaking timer effect
  useEffect(() => {
    let interval;
    if (isMatchmakingActive) {
      interval = setInterval(() => {
        setShowNoMatchesPopup(prev => {
          const newTime = prev + 1;
          // Show no matches popup after 60 seconds
          if (newTime >= TIMER_CONFIG.MATCHMAKING_DURATION_SECONDS) {
            // Automatically leave the room when max timer is reached
            callLeaveRoomAPI('max-timer-reached');
            leaveMeeting();
            
            setShowNoMatchesPopup(true);
            setIsMatchmakingActive(false);
            return 0;
          }
          return newTime;
        });
      }, 1000);
    } else {
      setShowNoMatchesPopup(false);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isMatchmakingActive]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (isChatOpen) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, isChatOpen, scrollToBottom]);

  // Reset chat when meeting changes
  useEffect(() => {
    setMessages([]);
    setUnreadCount(0);
    setIsChatOpen(false);
    setIsTyping(false);
    setRemoteUserTyping(false);
    
    // Clear timers
    if (chatAutoCloseTimer.current) {
      clearTimeout(chatAutoCloseTimer.current);
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  }, [meetingId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chatAutoCloseTimer.current) {
        clearTimeout(chatAutoCloseTimer.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

    };
  }, []);

  // Handle page unload (browser close, tab close, navigation away, reload)
  const handleBeforeUnload = useCallback((event) => {
    // Get current values from ref
    const {
      meetingId: currentMeetingId,
      remoteParticipantId: currentRemoteParticipantId,
      timerActive: currentTimerActive,
      localParticipantId: currentLocalParticipantId,
      leaveMeeting: currentLeaveMeeting
    } = currentStateRef.current;
    
    // Check user interaction to prevent cleanup on programmatic navigation
    const hasUserInteracted = localStorage.getItem('user_has_interacted') === 'true';
    
    if (hasUserInteracted && currentMeetingId) {
      // Use fetch with keepalive for reliable API call during page unload
      // fetch with keepalive can send custom headers unlike sendBeacon
      try {
        const token = currentStateRef.current.token;
        
        const leaveData = { 
          roomId: currentMeetingId,
          participantId: currentLocalParticipantId,
          reason: 'browser-close',
          timestamp: Date.now()
        };
        
        // Use fetch with keepalive - this allows custom headers unlike sendBeacon
        fetch(`${API_BASE_URL}/api/rooms/${currentMeetingId}/leave`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify(leaveData),
          keepalive: true // This ensures the request completes even if page is unloading
        }).catch(error => {
          // Fallback: try sendBeacon
          navigator.sendBeacon(`${API_BASE_URL}/api/rooms/${currentMeetingId}/leave`, JSON.stringify(leaveData));
        });
        
      } catch (error) {
        // Final fallback: try a simple GET request that doesn't require auth (if backend supports it)
        try {
          navigator.sendBeacon(`${API_BASE_URL}/api/rooms/${currentMeetingId}/leave-simple?reason=browser-close&participantId=${currentLocalParticipantId}&timestamp=${Date.now()}`);
        } catch (fallbackError) {
          // Silent fail
        }
      }
      
      // Also try regular VideoSDK leave
      if (currentLeaveMeeting && typeof currentLeaveMeeting === 'function') {
        try {
          currentLeaveMeeting();
        } catch (error) {
          // Silent fail
        }
      }
    }
  }, [currentStateRef]);

  // Set up browser unload event listeners
  useEffect(() => {
    // Track user interaction to ensure cleanup only happens for real user actions
    const handleUserInteraction = () => {
      localStorage.setItem('user_has_interacted', 'true');
    };
    
    // Set up user interaction tracking
    document.addEventListener('click', handleUserInteraction, { once: true });
    document.addEventListener('keydown', handleUserInteraction, { once: true });
    document.addEventListener('touchstart', handleUserInteraction, { once: true });
    
    // Add event listener for page unload
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Also add unload event as fallback
    const handleUnload = () => {
      // Fallback unload handler
    };
    window.addEventListener('unload', handleUnload);
    
    // Cleanup function
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, [handleBeforeUnload]);

  // Upload background handler
  const handleBackgroundUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file (JPEG, PNG, etc.)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    setUploadError('');

    try {
      const response = await authAPI.uploadBackground(file);
          
      // Add the new background to custom backgrounds
      const newBackground = {
        id: `custom_bg_${customBackgrounds.length}`,
        name: `Custom Background ${customBackgrounds.length + 1}`,
        preview: `${API_BASE_URL}${response.imageUrl || response.backgroundImage}`,
        fullUrl: `${API_BASE_URL}${response.imageUrl || response.backgroundImage}`,
        isCustom: true
      };
      
      setCustomBackgrounds(prev => {
        const updated = [...prev, newBackground];
        // Save to localStorage
        localStorage.setItem('customBackgrounds', JSON.stringify(updated));
        return updated;
      });
      
      // Track upload success
      trackEvent(ANALYTICS_EVENTS.BACKGROUND_UPLOAD_SUCCESS, {
        background_id: newBackground.id,
        file_size: file.size,
        file_type: file.type,
        timestamp: Date.now()
      });
      
    } catch (error) {
      console.error('Background upload failed:', error);
      setUploadError('Failed to upload background. Please try again.');
      
      // Track upload failure
      trackEvent(ANALYTICS_EVENTS.BACKGROUND_UPLOAD_FAILED, {
        error: error.message,
        file_size: file.size,
        file_type: file.type,
        timestamp: Date.now()
      });
    } finally {
      setIsUploading(false);
      // Clear the file input
      event.target.value = '';
    }
  };

  // Load custom backgrounds on component mount
  useEffect(() => {
    const loadCustomBackgrounds = async () => {
      try {
        // First try to load from localStorage
        const savedCustomBackgrounds = localStorage.getItem('customBackgrounds');
        if (savedCustomBackgrounds) {
          const parsed = JSON.parse(savedCustomBackgrounds);
          setCustomBackgrounds(parsed);
        }
        
        // Then load from API to get any new ones
        const response = await authAPI.getUserBackgrounds();
        
        if (response.backgroundImages && Array.isArray(response.backgroundImages)) {
          const customBgs = response.backgroundImages.map((imagePath, index) => ({
            id: `custom_bg_${index}`,
            name: `Custom Background ${index + 1}`,
            preview: `${API_BASE_URL}${imagePath}`,
            fullUrl: `${API_BASE_URL}${imagePath}`,
            isCustom: true
          }));
          
          // Save to localStorage
          localStorage.setItem('customBackgrounds', JSON.stringify(customBgs));
          
          setCustomBackgrounds(customBgs);
        }
      } catch (error) {
        console.error('Failed to load custom backgrounds:', error);
      }
    };

    loadCustomBackgrounds();
  }, []);

  // Matchmaking timer
  useInterval(() => {
    if (isMatchmakingActive) {
      setMatchmakingTimer(prev => {
        const newTime = prev + 1;
        // Show no matches popup after 60 seconds
        if (newTime >= TIMER_CONFIG.MATCHMAKING_DURATION_SECONDS) {
          // Automatically leave the room when max timer is reached
          callLeaveRoomAPI('max-timer-reached');
          leaveMeeting();
          
          setShowNoMatchesPopup(true);
          setIsMatchmakingActive(false);
          return 0;
        }
        return newTime;
      });
    } else {
      setMatchmakingTimer(0);
    }
  }, isMatchmakingActive ? 1000 : null);

  // Detect when remote participant leaves and trigger matchmaking
  useEffect(() => {
    // If we had a remote participant but now we don't, they left
    if (remoteParticipantId && participantIds.length === 1) {
      console.log('[Remote Leave Debug] Remote participant left, triggering matchmaking');
      
      // Add a delay to ensure the participant list has stabilized
      const handleRemoteLeave = async () => {
        try {
          // Track analytics event
          trackEvent(ANALYTICS_EVENTS.REMOTE_PARTICIPANT_LEFT, {
            room_id: meetingId,
            timestamp: Date.now()
          });
          
          // Call leave room API
          await callLeaveRoomAPI('remote-left');
          
          // Leave the meeting
          leaveMeeting();
          
          // Trigger re-matchmaking
          onBack({
            timerComplete: true,
            stayOnPage: true,
            reason: 'remote-left'
          });
        } catch (error) {
          console.error('[Remote Leave Debug] Error during remote leave:', error);
        }
      };
      
      // Add a 2-second delay to ensure participant list is stable
      setTimeout(handleRemoteLeave, 2000);
    }
  }, [participantIds, remoteParticipantId, meetingId, callLeaveRoomAPI, leaveMeeting, onBack]);

  // Additional detection for when remote participant disappears
  useEffect(() => {
    // If we had a remote participant but now remoteParticipantId is null/undefined
    if (remoteParticipantId === null && participantIds.length === 1) {
      console.log('[Remote Disconnect Debug] Remote participant disconnected, triggering matchmaking');
      
      // Track analytics event
      trackEvent(ANALYTICS_EVENTS.REMOTE_PARTICIPANT_LEFT, {
        room_id: meetingId,
        reason: 'disconnect',
        timestamp: Date.now()
      });
      
      // Call leave room API
      callLeaveRoomAPI('remote-disconnect');
      
      // Leave the meeting
      leaveMeeting();
      
      // Trigger re-matchmaking
      onBack({
        timerComplete: true,
        stayOnPage: true,
        reason: 'remote-disconnect'
      });
    }
  }, [remoteParticipantId, participantIds, meetingId, callLeaveRoomAPI, leaveMeeting, onBack]);

  // Robust detection using participants map
  useEffect(() => {
    // Check if we have a local participant but no remote participant in the map
    const localParticipantExists = localParticipantId && participants.has(localParticipantId);
    const remoteParticipantExists = remoteParticipantId && participants.has(remoteParticipantId);
    
    // If we have a local participant but no remote participant, and we're not in initial matchmaking
    if (localParticipantExists && !remoteParticipantExists && !isInitialMatchmaking && !isMatchmakingActive) {
      console.log('[Robust Detection Debug] Remote participant not found in participants map, triggering matchmaking');
      
      // Track analytics event
      trackEvent(ANALYTICS_EVENTS.REMOTE_PARTICIPANT_LEFT, {
        room_id: meetingId,
        reason: 'map-detection',
        timestamp: Date.now()
      });
      
      // Call leave room API
      callLeaveRoomAPI('remote-map-detection');
      
      // Leave the meeting
      leaveMeeting();
      
      // Trigger re-matchmaking
      onBack({
        timerComplete: true,
        stayOnPage: true,
        reason: 'remote-map-detection'
      });
    }
  }, [participants, localParticipantId, remoteParticipantId, isInitialMatchmaking, isMatchmakingActive, meetingId, callLeaveRoomAPI, leaveMeeting, onBack]);

  return (
    <div className="chat-page">
      {/* Initial Matchmaking Overlay - shows when app loads and no remote participant */}
      {isInitialMatchmaking && !remoteParticipantId && !showNoMatchesPopup && (
        <div className="matchmaking-overlay">
          <div className="matchmaking-content">
            <div className="matchmaking-spinner"></div>
            <h2>Finding Your Match...</h2>
            <p>Please wait while we connect you with someone</p>
            <p style={{ 
              marginTop: '10px', 
              fontSize: '18px', 
              fontWeight: 'bold',
              color: '#FF9800'
            }}>
              {initialMatchmakingCountdown}s
            </p>
            <div style={{ 
              marginTop: '20px', 
              padding: '10px', 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '8px',
              fontSize: '14px',
              opacity: 0.8
            }}>
              Meeting ID: {meetingId || 'Loading...'}
            </div>
          </div>
        </div>
      )}

      {/* Skip Matchmaking Overlay - shows when user clicks skip */}
      {isMatchmakingActive && !showNoMatchesPopup && (
        <div className="matchmaking-overlay">
          <div className="matchmaking-content">
            <div className="matchmaking-spinner"></div>
            <h2>Finding New Match...</h2>
            <p>{matchmakingTimer}s</p>
          </div>
        </div>
      )}

      {/* No Matches Popup */}
      {showNoMatchesPopup && (
        <div className="no-matches-overlay">
          <div className="no-matches-content">
            <h2>No Matches Found</h2>
            <p>Try again in a moment</p>
            <button 
              onClick={async () => {
                setShowNoMatchesPopup(false);
                
                // Track analytics event
                trackEvent(ANALYTICS_EVENTS.TRY_AGAIN_CLICK, {
                  room_id: meetingId,
                  timestamp: Date.now()
                });
                
                // Start fresh matchmaking
                onBack({
                  timerComplete: true,
                  stayOnPage: true,
                  reason: 'try-again'
                });
              }}
              className="retry-button"
            >
              Try Again
            </button>
          </div>
        </div>
      )}



      {/* Opponent Name Display - Top Left */}
      <div style={{
        position: 'fixed',
        top: '20px',
        left: '20px',
        zIndex: 1000,
        background: remoteParticipantId ? 
          'linear-gradient(135deg, rgba(255, 65, 108, 0.9), rgba(255, 75, 43, 0.9))' : 
          'linear-gradient(135deg, rgba(100, 100, 100, 0.7), rgba(60, 60, 60, 0.7))',
        backdropFilter: 'blur(15px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '20px',
        padding: '12px 20px',
        color: 'white',
        fontWeight: '600',
        fontSize: '16px',
        letterSpacing: '0.5px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
        maxWidth: '200px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        animation: 'fadeInFromLeft 0.8s ease-out',
        transition: 'all 0.3s ease'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: remoteParticipantId ? '#00ff00' : '#888',
            boxShadow: remoteParticipantId ? '0 0 10px rgba(255, 68, 68, 0.6)' : 'none',
            animation: remoteParticipantId ? 'pulse 2s infinite' : 'none'
          }} />
          {remoteParticipantId ? (
            <OpponentNameDisplay remoteParticipant={remoteParticipant} />
          ) : (
            'Waiting...'
          )}
        </div>
      </div>

      {/* User Name Display - Top Right */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 1000,
        background: 'linear-gradient(135deg, rgba(138, 43, 226, 0.9), rgba(30, 144, 255, 0.9))',
        backdropFilter: 'blur(15px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '20px',
        padding: '12px 20px',
        color: 'white',
        fontWeight: '600',
        fontSize: '16px',
        letterSpacing: '0.5px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
        maxWidth: '200px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        animation: 'fadeInFromRight 0.8s ease-out'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: '#00ff88',
            boxShadow: '0 0 10px rgba(0, 255, 136, 0.6)',
            animation: 'pulse 2s infinite'
          }} />
          <UserNameDisplay />
        </div>
      </div>

      {/* Timer Display - Top Center */}
      {timerActive && !isMatchmakingActive && !isInitialMatchmaking && !showNoMatchesPopup && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          background: timeRemaining <= TIMER_CONFIG.WARNING_THRESHOLD_SECONDS 
            ? 'rgba(255, 50, 50, 0.9)' 
            : 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
          borderRadius: '15px',
          padding: '12px 24px',
          color: 'white',
          fontWeight: '600',
          fontSize: '24px',
          textAlign: 'center',
          border: timeRemaining <= TIMER_CONFIG.WARNING_THRESHOLD_SECONDS 
            ? '2px solid rgba(255, 100, 100, 0.8)' 
            : '1px solid rgba(255, 255, 255, 0.2)',
          fontFamily: 'monospace',
          animation: timeRemaining <= TIMER_CONFIG.WARNING_THRESHOLD_SECONDS ? 'timerPulse 1s ease-in-out infinite' : 'none',
          boxShadow: timeRemaining <= TIMER_CONFIG.WARNING_THRESHOLD_SECONDS 
            ? '0 0 20px rgba(255, 50, 50, 0.6), 0 0 40px rgba(255, 50, 50, 0.3)' 
            : 'none',
          transition: 'all 0.3s ease'
        }}>
          {formatTime(timeRemaining)}
        </div>
      )}



      {/* Audio components for all participants */}
      {participantIds.map(id => (
        <ParticipantAudio key={`audio-${id}`} participantId={id} />
      ))}

      {/* Left Avatar Section - Opponent (VRM) */}
      {!isMobileView && (
        <div className="avatar-section left-avatar">
          {/* Background image - shows remote user's background only when remote user exists */}
          <div 
            className={`bg-image ${remoteParticipantId ? 'remote-bg' : ''}`}
            style={remoteParticipantId ? getBackgroundStyle(remoteUserBackground) : { backgroundImage: 'url(/images/backgrounds/bg2.jpg)' }}
          />
        {isMatchmakingActive ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#fff',
            textAlign: 'center',
            padding: '40px',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '15px',
            border: '2px dashed #555'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px', animation: 'pulse 1.5s infinite' }}>🔄</div>
            <h3 style={{ color: '#fff', marginBottom: '10px', fontSize: '18px', fontWeight: '600' }}>
              Finding New Match...
            </h3>
            <p style={{ color: '#ccc', fontSize: '14px', lineHeight: '1.4' }}>
              Please wait while we connect you with the next person.
            </p>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#888',
            textAlign: 'center',
            padding: '40px',
            background: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '15px',
            border: '2px dashed #444'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '20px',
              opacity: 0.6
            }}>
              👤
            </div>
            <h3 style={{
              color: '#fff',
              marginBottom: '10px',
              fontSize: '18px',
              fontWeight: '600'
            }}>
              Waiting for Remote User
            </h3>
            <p style={{
              color: '#aaa',
              fontSize: '14px',
              lineHeight: '1.4',
              marginBottom: '15px'
            }}>
              Share your meeting ID with someone to start chatting together!
            </p>
            <div style={{
              background: 'rgba(255, 138, 0, 0.2)',
              color: '#ff8a00',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '12px',
              fontFamily: 'monospace',
              fontWeight: 'bold',
              letterSpacing: '1px'
            }}>
              {meetingId || 'Loading...'}
            </div>
          </div>
        )}
        </div>
      )}

      {/* Live2DViewer handles both local and remote models, positioned to cover both avatar areas */}
     

      {/* Right Avatar Section - User (VRM) - now only for background */}
      {/* <div className="avatar-section right-avatar"> */}
        {/* Background image - shows local user's background */}
        {/* <div 
          className="bg-image user-bg"
          style={getBackgroundStyle(selectedBackground)}
        /> */}
        {/* Models handled by Live2DViewer placed outside this section */}
      {/* </div> */}
      {!isMobileView && (
        <div className="avatar-section right-avatar">
          <div 
            className="bg-image user-bg"
            style={getBackgroundStyle(selectedBackground)}
          /> 
        </div>
      )}


      {/* Desktop Live2D Viewer */}
      {!isMobileView && (
        <Live2DViewer 
          participantId={localParticipantId}
          remoteParticipantId={remoteParticipantId}
          localAvatarPath={localLive2dPath}
          remoteAvatarPath={remoteLive2dPath}
        />
      )}

      {/* Mobile Live2D Viewer - overlays desktop version on mobile */}
      {isMobileView && (
        <Live2DViewerMobile
          participantId={localParticipantId}
          remoteParticipantId={remoteParticipantId}
          localAvatarPath={localLive2dPath}
          remoteAvatarPath={remoteLive2dPath}
          localBackground={selectedBackground}
          remoteBackground={remoteUserBackground}
          availableBackgrounds={availableBackgrounds}
        />
      )}
      
      {/* Debug info */}
      {console.log('[ChatPage] Live2DViewer props:', {
        localParticipantId,
        remoteParticipantId,
        localAvatarPath: localLive2dPath,
        remoteAvatarPath: remoteLive2dPath
      })}

      {/* Reactions from Remote User (Left side - Remote user area) */}
      {remoteReactions.map((reaction) => (
        <ReactionParticles 
          key={reaction.id}
          reaction={reaction.reaction}
          side="left"
          onComplete={() => {
            setRemoteReactions(prev => prev.filter(r => r.id !== reaction.id));
          }}
        />
      ))}

      {/* Reactions from Local User (Right side - Local user area) */}
      {localReactions.map((reaction) => (
        <ReactionParticles 
          key={reaction.id}
          reaction={reaction.reaction}
          side="right"
          onComplete={() => {
            setLocalReactions(prev => prev.filter(r => r.id !== reaction.id));
          }}
        />
      ))}

      {/* Stunning Transparent Chat Overlay */}
      <div className={`chat-overlay ${isChatOpen ? 'chat-open' : ''}`}>
        {/* Floating Chat Toggle Button */}
        <div 
          className={`chat-toggle-btn ${isChatOpen ? 'open' : ''}`}
          onClick={toggleChat}
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            width: '70px',
            height: '70px',
            background: isChatOpen ? 
              'linear-gradient(135deg, rgba(255, 100, 100, 0.9), rgba(255, 50, 150, 0.9))' :
              'linear-gradient(135deg, rgba(0, 200, 255, 0.9), rgba(100, 150, 255, 0.9))',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            backdropFilter: 'blur(20px)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            boxShadow: isChatOpen ? 
              '0 20px 40px rgba(255, 100, 100, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2) inset' :
              '0 20px 40px rgba(0, 200, 255, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2) inset',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 2000,
            animation: 'float 3s ease-in-out infinite'
          }}
        >
          {isChatOpen ? (
            <div style={{
              color: '#fff',
              fontSize: '28px',
              fontWeight: 'bold',
              transition: 'all 0.3s ease',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>
              ✕
            </div>
          ) : (
            <img 
              src="/images/icons/chat.svg" 
              alt="Chat" 
              style={{ 
                width: '32px', 
                height: '32px',
                filter: 'brightness(0) invert(1)',
                transition: 'all 0.3s ease'
              }}
            />
          )}
          
          {/* Unread Badge */}
          {!isChatOpen && unreadCount > 0 && (
            <div style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              background: 'linear-gradient(135deg, #ff4444, #cc0000)',
              color: '#fff',
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(255, 68, 68, 0.6)',
              animation: 'bounceIn 0.6s ease-out, pulse 2s infinite 1s'
            }}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </div>
          )}
        </div>

        {/* Chat Panel */}
        {isChatOpen && (
          <div 
            className="chat-panel"
            style={{
              position: 'fixed',
              bottom: '120px',
              right: '30px',
              transform: 'none',
              width: '450px',
              height: '600px',
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '30px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              zIndex: 1500,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              opacity: 1,
              animation: 'none'
            }}
          >
            {/* Chat Header */}
            <div style={{
              padding: '25px 30px',
              background: 'rgba(0, 0, 0, 0.15)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: remoteParticipantId ? 
                    'linear-gradient(135deg, #00ff88, #00cc66)' : 
                    'linear-gradient(135deg, #888, #666)',
                  boxShadow: remoteParticipantId ? '0 0 15px rgba(0, 255, 136, 0.8)' : 'none',
                  animation: remoteParticipantId ? 'pulse 2s infinite' : 'none'
                }} />
                <h3 style={{
                  color: '#fff',
                  fontSize: '20px',
                  fontWeight: '700',
                  margin: 0,
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  letterSpacing: '0.5px'
                }}>
                  Live Chat
                </h3>
                <span style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {remoteParticipantId ? '• Connected' : '• Waiting...'}
                </span>
              </div>
            </div>

            {/* Messages Area */}
            <div style={{
              flex: 1,
              padding: '20px 30px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent'
            }}>
              {messages.length === 0 ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '16px',
                  textAlign: 'center',
                  gap: '15px'
                }}>
                  <div style={{ 
                    fontSize: '48px', 
                    opacity: 0.8,
                    background: 'linear-gradient(135deg, #64b5f6, #42a5f5)',
                    borderRadius: '50%',
                    width: '80px',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'float 3s ease-in-out infinite'
                  }}>💭</div>
                  <h4 style={{ margin: 0, color: '#fff', fontSize: '18px' }}>No messages yet</h4>
                  <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>Start a conversation!</p>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    style={{
                      display: 'flex',
                      flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                      alignItems: 'flex-end',
                      gap: '12px',
                      animation: `slideInMessage 0.4s ease-out`
                    }}
                  >
                    <div style={{
                      background: message.sender === 'user' ? 
                        'linear-gradient(135deg, #667eea, #764ba2)' : 
                        'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
                      color: '#fff',
                      padding: '15px 20px',
                      borderRadius: message.sender === 'user' ? '25px 25px 8px 25px' : '25px 25px 25px 8px',
                      maxWidth: '320px',
                      wordBreak: 'break-word',
                      fontSize: '15px',
                      lineHeight: '1.5',
                      boxShadow: message.sender === 'user' ? 
                        '0 8px 25px rgba(102, 126, 234, 0.4)' : 
                        '0 8px 25px rgba(0, 0, 0, 0.2)',
                      border: message.sender !== 'user' ? '1px solid rgba(255, 255, 255, 0.25)' : 'none',
                      backdropFilter: 'blur(10px)',
                      position: 'relative'
                    }}>
                      <div>{message.text}</div>
                      <div style={{
                        fontSize: '11px',
                        opacity: 0.8,
                        marginTop: '6px',
                        textAlign: message.sender === 'user' ? 'right' : 'left'
                      }}>
                        {message.displayTime || message.timestamp || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))
              )}
              
              {/* Typing indicator */}
              {remoteUserTyping && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '12px',
                  animation: `slideInMessage 0.3s ease-out`
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
                    color: 'rgba(255, 255, 255, 0.9)',
                    padding: '15px 20px',
                    borderRadius: '25px 25px 25px 8px',
                    fontSize: '15px',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '18px' }}>💭</span>
                    <span>Typing</span>
                    <div style={{ display: 'flex', gap: '3px' }}>
                      <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'currentColor', animation: 'typingDot 1.5s infinite' }} />
                      <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'currentColor', animation: 'typingDot 1.5s infinite 0.2s' }} />
                      <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'currentColor', animation: 'typingDot 1.5s infinite 0.4s' }} />
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Input */}
            <div style={{
              padding: '25px 30px',
              background: 'rgba(0, 0, 0, 0.15)',
              borderTop: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
              <div style={{
                display: 'flex',
                gap: '15px',
                alignItems: 'flex-end'
              }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <textarea
                    value={inputMessage}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder={remoteParticipantId ? "Type your message..." : "Waiting for someone to join..."}
                    disabled={!remoteParticipantId}
                    style={{
                      width: '100%',
                      minHeight: '50px',
                      maxHeight: '100px',
                      padding: '15px 60px 15px 20px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '2px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '25px',
                      color: '#fff',
                      fontSize: '15px',
                      resize: 'none',
                      outline: 'none',
                      fontFamily: 'inherit',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                      e.target.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                    }}
                    onBlur={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    }}
                  />
                  <button
                    onClick={() => {
                      trackEvent(ANALYTICS_EVENTS.SEND_MESSAGE_CLICK, {
                        room_id: meetingId,
                        message_length: inputMessage.trim().length,
                        timestamp: Date.now()
                      });
                      sendMessage();
                    }}
                    disabled={!inputMessage.trim() || !remoteParticipantId || inputMessage.length > CHAT_CONFIG.MAX_MESSAGE_LENGTH}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: inputMessage.trim() && remoteParticipantId && inputMessage.length <= CHAT_CONFIG.MAX_MESSAGE_LENGTH ? 
                        'linear-gradient(135deg, #667eea, #764ba2)' : 
                        'rgba(255, 255, 255, 0.1)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '16px',
                      cursor: inputMessage.trim() && remoteParticipantId ? 'pointer' : 'not-allowed',
                      transition: 'all 0.3s ease',
                      opacity: inputMessage.trim() && remoteParticipantId && inputMessage.length <= CHAT_CONFIG.MAX_MESSAGE_LENGTH ? 1 : 0.5,
                      boxShadow: inputMessage.trim() && remoteParticipantId ? '0 8px 20px rgba(102, 126, 234, 0.4)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (inputMessage.trim() && remoteParticipantId) {
                        e.target.style.transform = 'translateY(-50%) scale(1.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(-50%) scale(1)';
                    }}
                  >
                    ➤
                  </button>
                  <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '60px',
                    fontSize: '11px',
                    color: inputMessage.length > CHAT_CONFIG.MAX_MESSAGE_LENGTH * 0.8 ? '#ff6b6b' : 'rgba(255, 255, 255, 0.5)'
                  }}>
                    {inputMessage.length}/{CHAT_CONFIG.MAX_MESSAGE_LENGTH}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Home Button - Left Bottom Corner */}
      <button 
        className="home-btn-corner"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Track analytics event
          trackEvent(ANALYTICS_EVENTS.HOME_BUTTON_CLICK, {
            room_id: meetingId,
            timestamp: Date.now()
          });
          
          // Show confirmation popup
          setShowHomeConfirmation(true);
        }}
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          backgroundColor: '#9C27B0',
          color: 'white',
          border: '2px solid rgba(156, 39, 176, 0.6)',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }}
        title="Go Home"
      >
        <span style={{ fontSize: '24px' }}>🏠</span>
      </button>

      {/* Bottom Footer Bar */}
      <div className="chat-input-bar">
        <div className="action-buttons" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          gap: '20px'
        }}>
          <button 
            className="action-btn mic-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleMicToggle();
            }}
            style={{
              backgroundColor: localMicOn ? '#4CAF50' : '#FFFFFF',
              color: localMicOn ? '#FFFFFF' : '#333',
              border: `2px solid ${localMicOn ? 'rgba(76, 175, 80, 0.6)' : 'rgba(255, 255, 255, 0.3)'}`,
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            title={localMicOn ? "Mute Microphone" : "Unmute Microphone"}
          >
            <img 
              src={localMicOn ? "/images/icons/unmute.svg" : "/images/icons/mute.svg"}
              alt={localMicOn ? "Unmute" : "Mute"} 
              style={{ width: '24px', height: '24px' }}
            />
          </button>
          <button 
            className="action-btn game-btn"
            onClick={() => {
              trackEvent(ANALYTICS_EVENTS.GAME_BUTTON_CLICK, {
                room_id: meetingId,
                timestamp: Date.now()
              });
              setShowGamePopup(true);
            }}
            style={{
              backgroundColor: '#2196F3',
              color: 'white',
              border: '2px solid rgba(33, 150, 243, 0.6)',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <span style={{ fontSize: '24px' }}>🎮</span>
          </button>
          <button 
            className="action-btn gift-btn"
            onClick={() => {
              trackEvent(ANALYTICS_EVENTS.GIFT_BUTTON_CLICK, {
                room_id: meetingId,
                timestamp: Date.now()
              });
              setShowGiftPopup(true);
            }}
            style={{
              backgroundColor: '#E91E63',
              color: 'white',
              border: '2px solid rgba(233, 30, 99, 0.6)',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <span style={{ fontSize: '24px' }}>🎁</span>
          </button>
          <button 
            className="action-btn background-btn"
            onClick={() => {
              trackEvent(ANALYTICS_EVENTS.BACKGROUND_BUTTON_CLICK, {
                room_id: meetingId,
                current_state: showBackgroundSelector ? 'open' : 'closed',
                action: showBackgroundSelector ? 'close' : 'open',
                timestamp: Date.now()
              });
              setShowBackgroundSelector(!showBackgroundSelector);
            }}
            style={{
              backgroundColor: '#FFF3C2',
              color: '#333',
              border: '2px solid rgba(255, 243, 194, 0.6)',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <span style={{ fontSize: '24px' }}>🎨</span>
          </button>
          <button 
            className="action-btn reactions-btn"
            onClick={() => {
              trackEvent(ANALYTICS_EVENTS.REACTIONS_BUTTON_CLICK, {
                room_id: meetingId,
                current_state: showReactions ? 'open' : 'closed',
                action: showReactions ? 'close' : 'open',
                timestamp: Date.now()
              });
              setShowReactions(!showReactions);
            }}
            style={{
              backgroundColor: '#FF9800',
              color: 'white',
              border: '2px solid rgba(255, 152, 0, 0.6)',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <span style={{ fontSize: '24px' }}>😊</span>
          </button>
          <button 
            className="action-btn skip-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSkip();
            }}
            disabled={hasRequestedSkip.current}
            style={{
              backgroundColor: hasRequestedSkip.current ? '#666' : '#006468',
              color: 'white',
              border: `2px solid ${hasRequestedSkip.current ? 'rgba(102, 102, 102, 0.6)' : 'rgba(0, 100, 104, 0.6)'}`,
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: hasRequestedSkip.current ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              animation: hasRequestedSkip.current ? 'none' : 'pulse 2s ease-in-out infinite',
              opacity: hasRequestedSkip.current ? 0.6 : 1
            }}
            title={hasRequestedSkip.current ? "Skipping..." : "Skip to next match"}
          >
            <img 
              src="/images/icons/skip.svg" 
              alt="Skip" 
              style={{ width: '24px', height: '24px' }}
            />
          </button>

        </div>
      </div>

      {/* Background Selector Popup */}
      {showBackgroundSelector && (
        <div className="background-selector-overlay">
          <div className="background-selector-popup">
            <div className="background-selector-header">
              <h3>Choose Background</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  trackEvent(ANALYTICS_EVENTS.BACKGROUND_CLOSE_CLICK, {
                    room_id: meetingId,
                    timestamp: Date.now()
                  });
                  setShowBackgroundSelector(false);
                }}
              >
                ×
              </button>
            </div>
            <div className="background-options">
              {/* Upload Section */}
              <div className="background-upload-section">
                <label htmlFor="background-upload" className="upload-button">
                  <div className="upload-icon">📁</div>
                  <span>Upload Custom Background</span>
                  <input
                    id="background-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleBackgroundUpload}
                    style={{ display: 'none' }}
                    disabled={isUploading}
                  />
                </label>
                {isUploading && (
                  <div className="upload-status">
                    <div className="upload-spinner"></div>
                    <span>Uploading...</span>
                  </div>
                )}
                {uploadError && (
                  <div className="upload-error">
                    {uploadError}
                  </div>
                )}
              </div>
              
              {/* Background Options */}
              {selectableBackgrounds.map((bg) => (
                <div 
                  key={bg.id}
                  className={`background-option ${selectedBackground === bg.id ? 'selected' : ''}`}
                  onClick={() => {
                    trackEvent(ANALYTICS_EVENTS.BACKGROUND_OPTION_CLICK, {
                      room_id: meetingId,
                      background_id: bg.id,
                      background_name: bg.name,
                      was_already_selected: selectedBackground === bg.id,
                      timestamp: Date.now()
                    });
                    handleBackgroundChange(bg.id);
                  }}
                >
                  <div 
                    className="background-preview"
                    style={{
                      backgroundImage: `url(${bg.preview})`,
                      backgroundColor: '#ddd'
                    }}
                  />
                  <span className="background-name">{bg.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Reactions Popup */}
      {showReactions && (
        <div style={{
          position: 'fixed',
          bottom: '120px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(0, 0, 0, 0.9)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          padding: '20px',
          border: '2px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
          zIndex: 2000,
          animation: 'popupSlideIn 0.3s ease-out'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '15px',
            maxWidth: '300px'
          }}>
            {['👍', '👎', '❤️', '👏', '😂', '😮', '🎉', '🔥'].map((reaction) => (
              <button
                key={reaction}
                onClick={() => {
                  // Send reaction to remote user
                  if (publishReaction && remoteParticipantId) {
                    const reactionData = {
                      reaction: reaction,
                      senderId: localParticipantId,
                      timestamp: Date.now()
                    };
                    publishReaction(reactionData);
                    
                    // Add to remote reactions area (left side) for the sender's view
                    setRemoteReactions(prev => [...prev, {
                      ...reactionData,
                      id: Date.now() + Math.random()
                    }]);
                  }
                  
                  // Close reactions popup
                  setShowReactions(false);
                  
                  trackEvent(ANALYTICS_EVENTS.REACTION_SENT, {
                    room_id: meetingId,
                    reaction: reaction,
                    timestamp: Date.now()
                  });
                }}
                style={{
                  width: '50px',
                  height: '50px',
                  fontSize: '24px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.1)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                {reaction}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Game Coming Soon Popup */}
      {showGamePopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.95), rgba(30, 136, 229, 0.95))',
            backdropFilter: 'blur(20px)',
            borderRadius: '25px',
            padding: '40px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
            animation: 'popupSlideIn 0.4s ease-out'
          }}>
            <div style={{
              fontSize: '64px',
              marginBottom: '20px',
              animation: 'bounce 2s infinite'
            }}>
              🎮
            </div>
            <h3 style={{
              color: 'white',
              fontSize: '28px',
              fontWeight: '600',
              margin: '0 0 15px 0',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>
              Games Coming Soon!
            </h3>
            <p style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '16px',
              lineHeight: '1.5',
              margin: '0 0 30px 0'
            }}>
              We're working on exciting multiplayer games for you to enjoy with your chat partner. Stay tuned!
            </p>
            <button
              onClick={() => {
                trackEvent(ANALYTICS_EVENTS.MODAL_CLOSE_CLICK, {
                  modal_type: 'game',
                  room_id: meetingId,
                  timestamp: Date.now()
                });
                setShowGamePopup(false);
              }}
              style={{
                background: 'linear-gradient(135deg, #fff, #f0f0f0)',
                color: '#2196F3',
                border: 'none',
                borderRadius: '15px',
                padding: '12px 30px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
                transition: 'all 0.3s ease',
                transform: 'translateY(0)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 12px 25px rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.2)';
              }}
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Gift Coming Soon Popup */}
      {showGiftPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.95), rgba(216, 27, 96, 0.95))',
            backdropFilter: 'blur(20px)',
            borderRadius: '25px',
            padding: '40px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
            animation: 'popupSlideIn 0.4s ease-out'
          }}>
            <div style={{
              fontSize: '64px',
              marginBottom: '20px',
              animation: 'bounce 2s infinite'
            }}>
              🎁
            </div>
            <h3 style={{
              color: 'white',
              fontSize: '28px',
              fontWeight: '600',
              margin: '0 0 15px 0',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>
              Gifts Coming Soon!
            </h3>
            <p style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '16px',
              lineHeight: '1.5',
              margin: '0 0 30px 0'
            }}>
              Send virtual gifts and surprises to make your conversations even more special. This feature is in development!
            </p>
            <button
              onClick={() => {
                trackEvent(ANALYTICS_EVENTS.MODAL_CLOSE_CLICK, {
                  modal_type: 'gift',
                  room_id: meetingId,
                  timestamp: Date.now()
                });
                setShowGiftPopup(false);
              }}
              style={{
                background: 'linear-gradient(135deg, #fff, #f0f0f0)',
                color: '#E91E63',
                border: 'none',
                borderRadius: '15px',
                padding: '12px 30px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
                transition: 'all 0.3s ease',
                transform: 'translateY(0)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0  12px 25px rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.2)';
              }}
            >
              Awesome!
            </button>
          </div>
        </div>
      )}

      {/* Home Confirmation Popup */}
      {showHomeConfirmation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.95), rgba(123, 31, 162, 0.95))',
            backdropFilter: 'blur(20px)',
            borderRadius: '25px',
            padding: '40px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
            animation: 'popupSlideIn 0.4s ease-out'
          }}>
            <div style={{
              fontSize: '64px',
              marginBottom: '20px'
            }}>
              🏠
            </div>
            <h3 style={{
              color: 'white',
              fontSize: '28px',
              fontWeight: '600',
              margin: '0 0 15px 0',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>
              Leave Chat Room?
            </h3>
            <p style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '16px',
              lineHeight: '1.5',
              margin: '0 0 30px 0'
            }}>
              Are you sure you want to leave this chat room? You'll be disconnected from your current conversation.
            </p>
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => setShowHomeConfirmation(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '15px',
                  padding: '12px 25px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(10px)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Track analytics event
                  trackEvent(ANALYTICS_EVENTS.HOME_CONFIRMATION_CLICK, {
                    room_id: meetingId,
                    timestamp: Date.now()
                  });
                  
                  // Call leave room API
                  await callLeaveRoomAPI('user-left');
                  
                  // Leave the meeting
                  leaveMeeting();
                  
                  // Navigate back to landing page (not matchmaking)
                  if (onBack) {
                    onBack({ 
                      timerComplete: false, 
                      stayOnPage: false, 
                      reason: 'home-button',
                      goToLanding: true 
                    });
                  }
                }}
                style={{
                  background: 'linear-gradient(135deg, #fff, #f0f0f0)',
                  color: '#9C27B0',
                  border: 'none',
                  borderRadius: '15px',
                  padding: '12px 25px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.3s ease',
                  transform: 'translateY(0)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 25px rgba(0, 0, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.2)';
                }}
              >
                Leave Room
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;